import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  ArrowUpRight,
  Play,
  Calendar,
  Sparkles,
  TrendingUp,
  Users,
  Zap,
  CheckCircle2,
  MessageSquare,
} from "lucide-react";
import { MaskedLine, Container, OverlineChip } from "./primitives";

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const dashY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const dashRotate = useTransform(scrollYProgress, [0, 1], [0, -3]);
  const dashScale = useTransform(scrollYProgress, [0, 1], [1, 0.94]);

  return (
    <section ref={ref} data-testid="hero" className="relative pt-32 md:pt-40 pb-24 overflow-hidden">
      {/* Ambient orbs */}
      <div className="orb bg-[#4F46E5] w-[640px] h-[640px] -top-40 -left-40" />
      <div className="orb bg-[#7C3AED] w-[520px] h-[520px] top-[10%] right-[-10%] opacity-40" />
      <div className="orb bg-[#22D3EE] w-[360px] h-[360px] bottom-[-10%] left-[30%] opacity-30" />

      {/* Grid backdrop */}
      <div className="absolute inset-0 grid-bg pointer-events-none" aria-hidden />

      {/* Grain */}
      <div className="relative grain">
        <Container className="relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
            {/* Left: text */}
            <div className="lg:col-span-6 flex flex-col gap-8">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8, ease }}
              >
                <OverlineChip>
                  <span className="text-white/50 mr-2">v3.2</span>
                  <span className="text-white">AI Autopilot is live</span>
                </OverlineChip>
              </motion.div>

              <h1 className="font-display text-white leading-[0.92] tracking-[-0.035em] text-[clamp(2.5rem,6.6vw,6.25rem)]">
                <MaskedLine delay={0.35}>Turn social</MaskedLine>{" "}
                <MaskedLine delay={0.45}>
                  <span className="font-serif-x italic text-white/85">conversations</span>
                </MaskedLine>
                <br />
                <MaskedLine delay={0.6}>into qualified</MaskedLine>{" "}
                <MaskedLine delay={0.72}>
                  <span className="text-gradient-accent">customers</span>
                </MaskedLine>{" "}
                <MaskedLine delay={0.85}>with AI.</MaskedLine>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.05, duration: 0.8, ease }}
                className="max-w-xl text-lg md:text-xl text-white/60 leading-relaxed"
              >
                Discover leads, analyze intent, draft personalized engagement and manage your entire outreach workflow — from one intelligent platform.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.7, ease }}
                className="flex flex-col sm:flex-row items-start sm:items-center gap-3"
              >
                <a
                  data-testid="hero-cta-primary"
                  href="#pricing"
                  className="group relative inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-3 text-[15px] font-medium hover:bg-white/90 transition-all"
                >
                  Start free trial
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  data-testid="hero-cta-demo"
                  href="#contact"
                  className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-5 py-3 text-[15px] font-medium hover:bg-white/[0.06] transition-all"
                >
                  <Calendar className="h-4 w-4" />
                  Book a demo
                </a>
                <a
                  data-testid="hero-cta-overview"
                  href="#screenshots"
                  className="group inline-flex items-center gap-2 rounded-full px-3 py-3 text-[15px] font-medium text-white/70 hover:text-white transition-colors"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 border border-white/10 group-hover:bg-white/10">
                    <Play className="h-3.5 w-3.5" fill="currentColor" />
                  </span>
                  Watch overview <span className="text-white/40 font-mono-x text-xs">2:14</span>
                </a>
              </motion.div>

              {/* Micro proof */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.9 }}
                className="flex items-center gap-6 pt-4"
              >
                <div className="flex -space-x-2">
                  {["1494790108377-be9c29b29330", "1500648767791-00dcc994a43e", "1507003211169-0a1dd7228f2d", "1506863530036-1efeddceb993"].map((id, i) => (
                    <div
                      key={id}
                      className="h-8 w-8 rounded-full ring-2 ring-[#050505] overflow-hidden bg-white/10"
                      style={{ zIndex: 10 - i }}
                    >
                      <img src={`https://images.unsplash.com/photo-${id}?w=80&h=80&fit=crop&q=80`} alt="" aria-hidden="true" loading="lazy" decoding="async" width="80" height="80" className="h-full w-full object-cover" />
                    </div>
                  ))}
                </div>
                <div className="flex flex-col">
                  <div className="text-[13px] text-white/80">
                    <span className="font-medium">2,847+ teams</span> shipping outreach faster
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-white/40 font-mono-x">
                    ★★★★★ · 4.9 on G2 & Product Hunt
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right: dashboard preview */}
            <div className="lg:col-span-6 relative">
              <motion.div
                style={{ y: dashY, rotate: dashRotate, scale: dashScale }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 1.1, ease }}
                className="relative"
              >
                <DashboardPreview />
              </motion.div>

              {/* Floating cards */}
              <FloatingCard
                delay={1.3}
                className="absolute -top-6 -left-6 md:-left-10 z-20"
                testId="floating-new-leads"
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] flex items-center justify-center">
                    <Users className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-mono-x">New leads today</div>
                    <div className="font-display text-2xl font-medium">+58</div>
                  </div>
                </div>
              </FloatingCard>

              <FloatingCard
                delay={1.5}
                className="absolute -bottom-4 -right-2 md:-right-8 z-20"
                testId="floating-intent"
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#22D3EE] to-[#3B82F6] flex items-center justify-center">
                    <TrendingUp className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-mono-x">Avg intent score</div>
                    <div className="font-display text-2xl font-medium">92<span className="text-white/40 text-base">%</span></div>
                  </div>
                </div>
              </FloatingCard>

              <FloatingCard
                delay={1.7}
                className="absolute top-1/2 -right-3 md:-right-10 z-20 -translate-y-1/2"
                testId="floating-ai-draft"
              >
                <div className="flex items-center gap-3">
                  <div className="relative h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-[#a78bfa]" />
                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-[#0a0a0d]" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-mono-x">Suggested reply</div>
                    <div className="text-[13px] font-medium">AI draft ready</div>
                  </div>
                </div>
              </FloatingCard>

              <FloatingCard
                delay={1.9}
                className="absolute -bottom-8 -left-4 md:-left-10 z-20"
                testId="floating-scheduled"
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                    <Zap className="h-4 w-4 text-amber-300" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-mono-x">Scheduled actions</div>
                    <div className="font-display text-2xl font-medium">12</div>
                  </div>
                </div>
              </FloatingCard>
            </div>
          </div>

          {/* Hero stats strip */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease }}
            className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 border-t border-white/10"
          >
            {[
              { k: "12.4M", l: "Conversations scanned" },
              { k: "480K+", l: "Qualified leads surfaced" },
              { k: "78%", l: "Reply-to-lead uplift" },
              { k: "9.2×", l: "Faster than manual" },
            ].map((it, i) => (
              <div
                key={it.l}
                className={`px-4 md:px-8 py-8 md:py-10 border-b md:border-b-0 md:border-r border-white/10 ${
                  i === 3 ? "md:border-r-0" : ""
                }`}
              >
                <div className="font-display text-3xl md:text-4xl tracking-[-0.03em]">{it.k}</div>
                <div className="mt-1 text-sm text-white/50">{it.l}</div>
              </div>
            ))}
          </motion.div>
        </Container>
      </div>
    </section>
  );
}

function FloatingCard({ children, className = "", delay = 0, testId }) {
  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 20, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.8, ease }}
      className={`glass rounded-2xl px-4 py-3 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] ${className}`}
    >
      {children}
    </motion.div>
  );
}

function DashboardPreview() {
  return (
    <div className="relative rounded-[24px] border border-white/10 bg-gradient-to-b from-[#0f172a]/60 to-[#080810]/80 backdrop-blur-md overflow-hidden ring-glow">
      {/* Window chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-white/[0.02]">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        </div>
        <div className="mx-auto text-[11px] font-mono-x text-white/40 tracking-wider">
          app.sociallead.ai / dashboard / lead-pipeline
        </div>
        <span className="ml-auto text-[10px] font-mono-x text-emerald-300/80">● live</span>
      </div>

      <div className="grid grid-cols-12 gap-3 p-3">
        {/* Sidebar */}
        <div className="col-span-2 hidden md:flex flex-col gap-2">
          {["Pipeline", "Feed", "Drafts", "Schedule", "Analytics", "Approvals"].map((s, i) => (
            <div
              key={s}
              className={`text-[10px] font-mono-x px-2 py-2 rounded-lg border ${
                i === 0
                  ? "bg-white/10 border-white/15 text-white"
                  : "bg-transparent border-transparent text-white/40"
              }`}
            >
              {s.toLowerCase()}
            </div>
          ))}
        </div>

        {/* Main */}
        <div className="col-span-12 md:col-span-10 flex flex-col gap-3">
          {/* KPI row */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: "1,248", l: "Leads", d: "+18%" },
              { k: "312", l: "Threads", d: "+6%" },
              { k: "94", l: "Approvals", d: "+22%" },
            ].map((it) => (
              <div key={it.l} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="text-[9px] uppercase tracking-[0.18em] text-white/40 font-mono-x">{it.l}</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <div className="font-display text-lg">{it.k}</div>
                  <div className="text-[10px] text-emerald-300 font-mono-x">{it.d}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 h-[110px] relative overflow-hidden">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x">Intent Signals · 7d</div>
              <div className="flex gap-1">
                <span className="text-[10px] font-mono-x px-1.5 py-0.5 rounded bg-white/5 text-white/60">1D</span>
                <span className="text-[10px] font-mono-x px-1.5 py-0.5 rounded bg-white text-black">7D</span>
                <span className="text-[10px] font-mono-x px-1.5 py-0.5 rounded bg-white/5 text-white/60">30D</span>
              </div>
            </div>
            <svg viewBox="0 0 400 60" className="mt-2 w-full h-[60px]">
              <defs>
                <linearGradient id="ga" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0,45 C40,30 60,50 90,32 C120,15 150,42 180,28 C210,15 240,38 270,22 C300,10 330,25 360,14 L400,10 L400,60 L0,60 Z"
                fill="url(#ga)"
              />
              <path
                d="M0,45 C40,30 60,50 90,32 C120,15 150,42 180,28 C210,15 240,38 270,22 C300,10 330,25 360,14 L400,10"
                fill="none"
                stroke="#a78bfa"
                strokeWidth="1.5"
              />
            </svg>
          </div>

          {/* Threads feed */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x mb-2">Live Threads</div>
              <div className="space-y-2">
                {[
                  { n: "@amelia_wu", h: "Looking for a CRM that doesn’t suck…", s: 94 },
                  { n: "@marcus.dev", h: "We need lead intent scoring, any tips?", s: 88 },
                  { n: "@theo.notion", h: "Anyone using AI for outbound replies?", s: 76 },
                ].map((t) => (
                  <div key={t.n} className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-full bg-white/10 flex items-center justify-center text-[9px] font-mono-x">
                      {t.n.slice(1, 3).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-white/50 font-mono-x">{t.n}</div>
                      <div className="text-[11px] text-white/85 truncate">{t.h}</div>
                    </div>
                    <div className="text-[10px] font-mono-x text-emerald-300">{t.s}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-gradient-to-br from-[#4F46E5]/15 to-transparent p-3">
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-[#a78bfa]" />
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#a78bfa] font-mono-x">AI Suggested Reply</div>
              </div>
              <div className="mt-2 text-[11px] leading-relaxed text-white/85">
                Hey Amelia — noticed you’re evaluating CRMs. We just launched thread-native pipelines with intent scoring. Happy to show it in a 15-min walk-through.
                <span className="cursor-blink text-white/90">▍</span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[10px] font-mono-x px-2 py-1 rounded-md bg-white text-black">Approve</span>
                <span className="text-[10px] font-mono-x px-2 py-1 rounded-md border border-white/15 text-white/70">Edit</span>
                <span className="ml-auto text-[10px] font-mono-x text-white/40">confidence 92%</span>
              </div>
            </div>
          </div>

          {/* Pipeline strip */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono-x mb-2">CRM Pipeline</div>
            <div className="grid grid-cols-4 gap-2">
              {[
                { c: "Discovery", n: 214, color: "bg-white/5" },
                { c: "Qualified", n: 87, color: "bg-[#4F46E5]/15" },
                { c: "Engaged", n: 42, color: "bg-[#7C3AED]/20" },
                { c: "Converted", n: 18, color: "bg-emerald-400/15" },
              ].map((s) => (
                <div key={s.c} className={`rounded-lg border border-white/10 p-2 ${s.color}`}>
                  <div className="text-[9px] font-mono-x text-white/50 uppercase tracking-widest">{s.c}</div>
                  <div className="mt-1 font-display text-base">{s.n}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
